import { NavigationContext } from '@react-navigation/native';
import { useContext } from 'react';

/**
 * Route names registered with `presentation: 'modal'` in the booking stack.
 * A card pushed on top of one of these is presented inside the modal's
 * container (iOS), so the modal has already inset the content below the status
 * bar and the screen must not add the top inset again.
 *
 * `fullScreenModal` routes (view-reservation, view-gift-card) are deliberately
 * excluded: they cover the status bar area themselves, so screens above them
 * still own their top inset.
 */
const MODAL_ROUTE_SEGMENTS = ['appointments', 'campaign'];

const isModalRoute = (routeName: string) =>
  MODAL_ROUTE_SEGMENTS.some(
    (segment) => routeName === segment || routeName.startsWith(`${segment}/`),
  );

/**
 * Whether the current screen sits on top of a modally-presented route in its
 * own stack.
 *
 * Screens reached from more than one entry point can't assume their own
 * presentation: the same route is a full-screen card when pushed from a plain
 * stack, but is nested inside the modal container when pushed from a screen
 * that was itself presented modally. Deriving this from navigation state keeps
 * the check at the presentation layer instead of inferring it from a business
 * flag (e.g. "is rebooking"), which silently breaks as soon as a new entry
 * point reaches the same screen.
 *
 * Safe to call outside a navigator (returns false) so shared components that
 * render in isolation, such as in tests, don't crash.
 *
 * @returns true when a modal route precedes the current screen in the stack.
 */
export const useIsPresentedOverModal = (): boolean => {
  const navigation = useContext(NavigationContext);
  const state = navigation?.getState?.();

  if (!state?.routes) return false;

  // Only routes below the current one matter — a modal further up the stack
  // hasn't been presented yet.
  return state.routes
    .slice(0, state.index)
    .some((route) => isModalRoute(route.name));
};
